import { useAuth } from '@clerk/clerk-react';
export class GoogleDriveClient {
    baseUrl;
    getToken;
    constructor(baseUrl, getToken) {
        this.baseUrl = baseUrl;
        this.getToken = getToken;
    }
    async makeRequest(endpoint, options = {}) {
        const token = await this.getToken();
        const response = await fetch(`${this.baseUrl}/api/admin/google-drive${endpoint}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                ...options.headers,
            },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
    }
    async makeFormDataRequest(endpoint, formData) {
        const token = await this.getToken();
        const response = await fetch(`${this.baseUrl}/api/admin/google-drive${endpoint}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
    }
    // OAuth Flow Methods
    async getAuthUrl() {
        return this.makeRequest('/auth-url');
    }
    async createIntegration(request) {
        return this.makeRequest('/integrations', {
            method: 'POST',
            body: JSON.stringify(request),
        });
    }
    // Integration Management
    async listIntegrations() {
        return this.makeRequest('/integrations');
    }
    async getIntegration(integrationId) {
        return this.makeRequest(`/integrations/${integrationId}`);
    }
    async updateIntegration(integrationId, updates) {
        return this.makeRequest(`/integrations/${integrationId}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    }
    async deleteIntegration(integrationId) {
        return this.makeRequest(`/integrations/${integrationId}`, {
            method: 'DELETE',
        });
    }
    async activateIntegration(integrationId) {
        return this.makeRequest(`/integrations/${integrationId}/activate`, {
            method: 'POST',
        });
    }
    async testIntegration(integrationId) {
        return this.makeRequest(`/integrations/${integrationId}/test`, {
            method: 'POST',
        });
    }
    // File Operations
    async listFiles(options) {
        const params = new URLSearchParams();
        if (options?.folderId)
            params.append('folderId', options.folderId);
        if (options?.query)
            params.append('query', options.query);
        if (options?.pageSize)
            params.append('pageSize', options.pageSize.toString());
        const queryString = params.toString();
        return this.makeRequest(`/files${queryString ? `?${queryString}` : ''}`);
    }
    async getFileMetadata(fileId) {
        return this.makeRequest(`/files/${fileId}/metadata`);
    }
    async downloadFile(fileId) {
        const token = await this.getToken();
        const response = await fetch(`${this.baseUrl}/api/admin/google-drive/files/${fileId}/download`, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        return response.blob();
    }
    async uploadFile(file, options) {
        const formData = new FormData();
        formData.append('file', file);
        if (options?.folderId)
            formData.append('folderId', options.folderId);
        if (options?.name)
            formData.append('name', options.name);
        return this.makeFormDataRequest('/files/upload', formData);
    }
    async updateFile(fileId, file) {
        const formData = new FormData();
        formData.append('file', file);
        return this.makeFormDataRequest(`/files/${fileId}`, formData);
    }
    async deleteFile(fileId) {
        return this.makeRequest(`/files/${fileId}`, {
            method: 'DELETE',
        });
    }
    async createFolder(name, parentId) {
        return this.makeRequest('/folders', {
            method: 'POST',
            body: JSON.stringify({ name, parentId }),
        });
    }
    async shareFile(fileId, email, role = 'reader') {
        return this.makeRequest(`/files/${fileId}/share`, {
            method: 'POST',
            body: JSON.stringify({ email, role }),
        });
    }
    async makeFilePublic(fileId) {
        return this.makeRequest(`/files/${fileId}/make-public`, {
            method: 'POST',
        });
    }
    async searchFiles(searchTerm, options) {
        const params = new URLSearchParams();
        params.append('q', searchTerm);
        if (options?.folderId)
            params.append('folderId', options.folderId);
        if (options?.mimeType)
            params.append('mimeType', options.mimeType);
        return this.makeRequest(`/search?${params.toString()}`);
    }
}
export function useGoogleDriveClient() {
    const { getToken } = useAuth();
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    return new GoogleDriveClient(baseUrl, getToken);
}
